import { Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconX } from '@/components/ui/icons';
import { cn, safeStorage } from '@/lib/utils';

type To = '/runs' | '/settings/$section' | '/approvals' | '/usage';

export interface AnnouncementCta {
  label: string;
  to?: To;
  params?: Record<string, string>;
  onClick?: () => void;
}

/**
 * Flat full-width notice strip (STYLE.md "Notice strip"): tinted surface with a left rule, icon, text and a small outline CTA.
 * With `dismissKey` it can be dismissed; the dismissal is remembered per browser.
 */
export function AnnouncementBanner({
  tone = 'info',
  icon,
  children,
  cta,
  dismissKey,
}: {
  tone?: 'info' | 'danger';
  icon?: ReactNode;
  children: ReactNode;
  cta?: AnnouncementCta;
  dismissKey?: string;
}) {
  const { t } = useTranslation();
  const storageKey = dismissKey ? `af:dismiss:${dismissKey}` : null;
  const [dismissed, setDismissed] = useState(() => (storageKey ? !!safeStorage.get(storageKey) : false));
  if (dismissed) return null;

  const pill =
    'inline-flex h-6 shrink-0 items-center rounded-md border border-border-strong bg-bg px-2.5 text-xs font-medium text-fg hover:bg-hover';
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      data-tone={tone}
      className="banner-strip relative flex min-h-9 shrink-0 items-center justify-center gap-x-3 gap-y-1 py-1.5 pr-11 pl-4 text-sm max-lg:justify-start max-lg:gap-x-2 max-lg:pl-3 max-lg:text-xs"
    >
      {icon ? (
        <span aria-hidden className="shrink-0 opacity-90">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 font-medium max-lg:line-clamp-2">{children}</span>
      {cta ? (
        cta.to ? (
          <Link to={cta.to} params={cta.params as never} className={pill}>
            {cta.label}
          </Link>
        ) : (
          <button type="button" onClick={cta.onClick} className={pill}>
            {cta.label}
          </button>
        )
      ) : null}
      {storageKey ? (
        <button
          type="button"
          aria-label={t('shell.dismiss')}
          title={t('shell.dismiss')}
          onClick={() => {
            safeStorage.set(storageKey, true);
            setDismissed(true);
          }}
          className={cn(
            'absolute top-1/2 right-2 inline-flex h-7 w-7 touch:h-9 touch:w-9 -translate-y-1/2 items-center justify-center rounded-md',
            'text-muted hover:bg-hover hover:text-fg',
          )}
        >
          <IconX size={14} />
        </button>
      ) : null}
    </div>
  );
}
